/**
 * CTO Dashboard API client
 * Calls go to the FastAPI backend (default: http://localhost:8600)
 */
import axios from 'axios'

const baseURL = import.meta.env.VITE_API_URL || ''

export const api = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
})

// Track 4.18: tenant switcher. Every request carries the user's chosen
// CDM org_id (if any) so the backend's _resolve_org_id can scope reads
// to the right tenant. Stored in localStorage under 'cdm-org-id'.
api.interceptors.request.use((config) => {
  try {
    if (typeof localStorage !== 'undefined') {
      const orgId = localStorage.getItem('cdm-org-id')
      if (orgId && config.headers) {
        // axios v1 uses AxiosHeaders or plain objects depending on init
        if (typeof (config.headers as any).set === 'function') {
          (config.headers as any).set('X-CDM-Org-Id', orgId)
        } else {
          (config.headers as any)['X-CDM-Org-Id'] = orgId
        }
      }
    }
  } catch {}
  return config
})

// Only redirect to /login when a user who HAS a token gets an explicit
// "Token expired" or "Invalid token" response. Never redirect on mere
// "Not authenticated" or "Invalid credentials" — those fire on background
// fetches (WhatsNewBadge, /api/me polls) that don't require a token to
// work, and kicking the user out on them turns every page blank for
// unauthenticated callers. Login flow's own 401s still surface as errors
// for the login form to display; they just don't boot the user out.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status
    const detail = (err?.response?.data?.detail || '').toString().toLowerCase()
    const hadToken = !!(typeof localStorage !== 'undefined' && localStorage.getItem('cto-token'))
    const tokenExpired = status === 401 && hadToken && (
      detail.includes('token expired') || detail.includes('invalid token')
    )
    if (tokenExpired) {
      try {
        localStorage.removeItem('cto-token')
        localStorage.removeItem('cto-email')
        delete api.defaults.headers.common['Authorization']
      } catch {}
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        const next = encodeURIComponent(window.location.pathname + window.location.search)
        window.location.href = `/login?next=${next}`
      }
    }
    return Promise.reject(err)
  },
)

// ── Signals ──────────────────────────────────────────────────────────────────

export async function fetchSignals(params?: {
  limit?: number
  source?: string
  signal_type?: string
  category?: string
}) {
  const { data } = await api.get('/api/signals/feed', { params })
  return data
}

export async function fetchSignalDigest(limit = 30) {
  const { data } = await api.get('/api/signals/digest', { params: { limit } })
  return data
}

export async function fetchConvergences(minSources = 2) {
  const { data } = await api.get('/api/signals/convergences', { params: { min_sources: minSources } })
  return data
}

export async function fetchSignalSources() {
  const { data } = await api.get('/api/signals/sources')
  return data
}

// ── Tech News ────────────────────────────────────────────────────────────────

export async function fetchTechNews(params?: { limit?: number; category?: string }) {
  const { data } = await api.get('/api/news/feed', { params })
  return data
}

export async function fetchBreakingNews() {
  const { data } = await api.get('/api/news/breaking')
  return data
}

// ── Cloud Status ─────────────────────────────────────────────────────────────

export async function fetchCloudStatus() {
  const { data } = await api.get('/api/cloud/status')
  return data
}

// ── Cyber ────────────────────────────────────────────────────────────────────

export async function fetchCyberFeed(params?: { limit?: number; severity?: string }) {
  const { data } = await api.get('/api/cyber/feed', { params })
  return data
}

export async function fetchCVEs(params?: { limit?: number }) {
  const { data } = await api.get('/api/cyber/cves', { params })
  return data
}

export async function fetchThreatLevel() {
  const { data } = await api.get('/api/cyber/threat-level')
  return data
}

// ── Innovation ───────────────────────────────────────────────────────────────

export async function fetchInnovationSignals(params?: { limit?: number; category?: string }) {
  const { data } = await api.get('/api/innovation/feed', { params })
  return data
}

export async function fetchGitHubTrending() {
  const { data } = await api.get('/api/innovation/github-trending')
  return data
}
